import { Message, Spinner } from 'Components/Core';
import { NamePrefixModifier } from 'Components/Core/InjectedPropsContext';
import Balance from 'Components/Equipment/Balances';
import CompressionMachine from 'Components/Equipment/CompressionMachine';
import EquipmentInjectedPropsProvider from 'Components/Equipment/Core/EquipmentInjectedPropsProvider';
import FixedEquipmentHeader from 'Components/Equipment/Core/Header/FixedEquipmentHeader';
import DeflectionGauge from 'Components/Equipment/DeflectionGauge';
import GroovingToolEquipment from 'Components/Equipment/GroovingTool';
import Hammer from 'Components/Equipment/Hammer';
import LiquidLimitApparatusEquipment from 'Components/Equipment/LiquidLimitApparatus';
import LoadCell from 'Components/Equipment/LoadCell';
import MoistureTin from 'Components/Equipment/MoistureTin';
import SieveEquipment from 'Components/Equipment/Sieves/SieveEquipment';
import SieveSetEquipment from 'Components/Equipment/SieveSet';
import { PageSection } from 'Components/Layout';
import Page from 'Components/Pages/Core/Page';
import { actions as appActions } from 'Data/ReduxViewModels/App';
import { TitleSource } from 'Data/ReduxViewModels/App/types';
import { actions } from 'Data/ReduxViewModels/Equipment';
import { hasNonEmptyValue } from 'Lib/Any';
import { combine } from 'Lib/Hooks/Selectors';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

const equipmentTypeComponentMapping: {
  [name: string]: () => JSX.Element;
} = {
  Sieve: SieveEquipment,
  'Sieve Set': SieveSetEquipment,
  'Grooving Tool': GroovingToolEquipment,
  'Liquid Limit Cup': LiquidLimitApparatusEquipment,
  Hammer: Hammer,
  Balance: Balance,
  'Compression Machine': CompressionMachine,
  'Load Cell': LoadCell,
  'Deflection Gauge': DeflectionGauge,
  'Moisture Tin': MoistureTin,
};

let EquipmentPage: React.FC = () => {
  const params = useParams();
  const equipmentId = params.equipmentId?.toLowerCase();
  const ready = useSelector((s) => s.equipment.status.ready);
  const dispatch = useDispatch();

  const equipmentType = useSelector((s) => (s.equipment.equipmentType as any)?.EquipmentType);
  const equipmentLoaded = useSelector((s) => !!s.equipment.syncModel);

  const EquipmentComponent = equipmentTypeComponentMapping[equipmentType];

  const InjectedPropsProvider = EquipmentInjectedPropsProvider;

  useEffect(() => {
    dispatch(actions.init({ equipmentId }));
  }, [dispatch, equipmentId]);

  const pageTitle = useSelector(
    combine(
      [
        (state) => (state.equipment.syncModel as any)?.data?.Code,
        (state) => (state.equipment.equipmentType as any)?.Name,
      ],
      (code, equipmentName) =>
        equipmentLoaded && ready ? [code, equipmentName].filter(hasNonEmptyValue).join(' - ') : undefined
    )
  );
  useEffect(() => {
    if (hasNonEmptyValue(pageTitle)) dispatch(appActions.setPageTitle({ pageTitle, source: TitleSource.Specific }));
    return () => {
      dispatch(appActions.setPageTitle({ pageTitle: undefined, source: TitleSource.Specific }));
    };
  }, [dispatch, pageTitle]);

  if (!ready) {
    return (
      <Page>
        <Spinner large />
      </Page>
    );
  } else if (!EquipmentComponent || !equipmentLoaded) {
    return (
      <Page>
        <PageSection>
          <Message warning>No equipment loaded.</Message>
        </PageSection>
      </Page>
    );
  } else {
    return (
      <Page>
        <div className="EquipmentPage">
          <InjectedPropsProvider>
            <NamePrefixModifier namePrefix={'data'}>
              <FixedEquipmentHeader />
              <div className="EquipmentPage-Content">
                <EquipmentComponent />
              </div>
            </NamePrefixModifier>
          </InjectedPropsProvider>
        </div>
      </Page>
    );
  }
};

export default EquipmentPage;
